import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select } from '@/components/ui/select'
import { UserAvatar } from '@/components/UserProfileCard'
import {
  useDelegateReview,
  useDeliverableSetsStore,
} from '@/features/deliverable-set'
import { useUserList } from '@/features/user'
import { ERoleType } from '@/features/user-management/constants'
import { useCurrentUser } from '@/hooks/useAuth.ts'
import type { User } from '@/schema'
import { formatFullName } from '@/utils/common'
import { DEFAULT_GET_ALL_LIMIT } from '@/utils/constants'

import { getReviewerUsers, getViewerUsers, isAssignedInProject } from '../utils'

import { NoteField } from './NoteField'

export interface DelegateFormData {
  changeToId: string
  note: string
}

export interface UserWithFullName extends User {
  fullName: string
}

const DelegateDialog: React.FC = () => {
  const { t } = useTranslation()

  const {
    showDelegateDialog,
    delegateDeliverableSet,
    setDelegateDialogOpen,
    clearDelegateDeliverableSet,
  } = useDeliverableSetsStore()
  const { data: currentUser } = useCurrentUser()

  const { data: { data = [] } = {}, isLoading: isLoadingUsers } = useUserList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    enabled: showDelegateDialog,
  })

  const delegateReviewMutation = useDelegateReview()

  const assignUserIds = useMemo(() => {
    const ids: string[] = []
    if (!delegateDeliverableSet) return ids

    const { users: reviewers } = getReviewerUsers(delegateDeliverableSet)
    const { users: viewers } = getViewerUsers(delegateDeliverableSet)

    const users = [...reviewers, ...viewers]
    users.forEach((u) => {
      if (u.id && u.id !== currentUser?.id) ids.push(u.id)
    })

    return ids
  }, [currentUser?.id, delegateDeliverableSet])

  const users = useMemo(() => {
    const result: UserWithFullName[] = []

    // Filter users based on the following criteria:
    // - Not the owner of the deliverable set
    // - Not an organization admin
    // - Not assigned in the project
    // - Not the current user
    // - Not in the assign user ids
    data.forEach((u) => {
      const isOwner = u.id === delegateDeliverableSet?.owner_id
      const isOrgAdmin = u.roles?.some(
        (r) => r.role_name === ERoleType.ORGANIZATION_ADMIN
      )
      const isAssignedForProject = u.roles?.some((r) =>
        isAssignedInProject(r, delegateDeliverableSet?.project_id)
      )

      if (
        !isOwner &&
        !isOrgAdmin &&
        !isAssignedForProject &&
        !assignUserIds.includes(u.id)
      ) {
        result.push({
          ...u,
          fullName: formatFullName(u.first_name, u.last_name),
        })
      }
    })

    return result
  }, [
    assignUserIds,
    data,
    delegateDeliverableSet?.owner_id,
    delegateDeliverableSet?.project_id,
  ])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<DelegateFormData>({
    defaultValues: { changeToId: '', note: '' },
    mode: 'onChange',
  })

  const noteWatch = watch('note')

  const isLoading = useMemo(() => isLoadingUsers, [isLoadingUsers])

  const isSaving = useMemo(
    () => delegateReviewMutation.isPending,
    [delegateReviewMutation.isPending]
  )

  useEffect(() => {
    if (!showDelegateDialog) {
      reset({ changeToId: '', note: '' })
      clearDelegateDeliverableSet()
      return
    }

    if (delegateDeliverableSet) {
      setValue('changeToId', currentUser?.id || '')
      setValue('note', '')
    }
  }, [
    clearDelegateDeliverableSet,
    currentUser?.id,
    delegateDeliverableSet,
    reset,
    setValue,
    showDelegateDialog,
  ])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setDelegateDialogOpen(open)
      if (!open) {
        clearDelegateDeliverableSet()
      }
    },
    [setDelegateDialogOpen, clearDelegateDeliverableSet]
  )

  const onSubmit = (data: DelegateFormData) => {
    if (!delegateDeliverableSet?.id) {
      return
    }

    delegateReviewMutation.mutate(
      {
        current_reviewer_id: currentUser?.id || '',
        deliverable_set_id: delegateDeliverableSet.id,
        new_reviewer_id: data.changeToId,
        note: data.note,
      },
      {
        onSuccess: () => {
          triggerToast('success', t('deliverableSet.delegate.messages.success'))
          handleOpenChange(false)
        },
        onError: () => {
          triggerToast('error', t('deliverableSet.delegate.messages.error'))
        },
      }
    )
  }

  return (
    <Dialog open={showDelegateDialog} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="!overflow-hidden flex flex-col max-h-[95dvh] !max-w-[400px]">
          <DialogHeader className="space-y-0.5 flex-shrink-0">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('deliverableSet.delegate.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 px-6 -mx-6 overflow-x-hidden overflow-y-auto">
            <form
              id="delegate-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <div className="space-y-1">
                <label htmlFor="changeTo" className="text-sm font-medium">
                  {t('deliverableSet.delegate.fields.changeTo')}
                </label>
                <Controller
                  name="changeToId"
                  control={control}
                  rules={{
                    required: t(
                      'deliverableSet.delegate.validation.changeToRequired'
                    ),
                  }}
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onChange={field.onChange}
                      disabled={isLoadingUsers || !users.length || isSaving}
                      fullWidth
                      placeholder={t(
                        'deliverableSet.dialog.placeholders.selectReviewer'
                      )}
                      clearable
                      options={users.map((user) => ({
                        value: user.id,
                        label: (
                          <div className="flex flex-col">
                            <div className="text-sm font-medium leading-5">
                              {user.fullName}
                            </div>
                            <div className="text-xs leading-4 text-gray-500">
                              {user.email}
                            </div>
                          </div>
                        ),
                        iconLeft: (
                          <UserAvatar
                            avatarUrl={user.avatar_url || ''}
                            userName={user.fullName}
                            className="size-7"
                            textClassName="text-xs"
                          />
                        ),
                      }))}
                    />
                  )}
                />
                {errors.changeToId && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.changeToId.message}
                  </p>
                )}
              </div>

              <NoteField
                control={control}
                name="note"
                disabled={isSaving}
                errors={errors}
                noteValue={noteWatch}
                maxNoteLength={255}
              />
            </form>
          </div>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3 flex-shrink-0">
            <Button
              type="button"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-[--Colors-Gray-light-700]"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="delegate-form"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent text-white bg-brand-solid hover:bg-brand-solid-hover"
              variant="default"
              disabled={isSaving || isLoading || !isValid}
            >
              {isSaving
                ? t('deliverableSet.delegate.actions.delegating')
                : t('deliverableSet.delegate.actions.delegate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DelegateDialog
